import React, { useState, useCallback } from "react";
import { RendererProps } from "./index";
import {
  ChoiceList,
  EntryCard,
  EntryCardRow,
  SelectWrapper,
  TextInput,
  FieldLabel,
  AddButton,
  RemoveButton,
} from "./renderer-styles";

interface Row {
  selectVal?: { value: string; title?: string };
  inputText: string;
}

function initRows(answer: RendererProps["answer"]): Row[] {
  if (answer?.value && Array.isArray(answer.value) && answer.value.length > 0) {
    return answer.value.map((v: any) => ({
      selectVal: v.selectVal,
      inputText: v.inputText ?? "",
    }));
  }
  return [{ inputText: "" }];
}

const ColumnsSelectNumberRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {

  const [rows, setRows] = useState<Row[]>(() => initRows(answer));

  const emit = useCallback(
    (nextRows: Row[]) => {
      const hasValid = nextRows.some(
        (r) => r.selectVal?.value !== undefined && r.inputText !== ""
      );
      onAnswer(nextRows, hasValid);
    },
    [onAnswer]
  );

  const updateRow = useCallback(
    (index: number, patch: Partial<Row>) => {
      setRows((prev) => {
        const next = prev.map((r, i) => (i === index ? { ...r, ...patch } : r));
        emit(next);
        return next;
      });
    },
    [emit]
  );

  const addRow = useCallback(() => {
    setRows((prev) => {
      const next = [...prev, { inputText: "" }];
      emit(next);
      return next;
    });
  }, [emit]);

  const removeRow = useCallback(
    (index: number) => {
      setRows((prev) => {
        const next = prev.filter((_, i) => i !== index);
        const result = next.length === 0 ? [{ inputText: "" }] : next;
        emit(result);
        return result;
      });
    },
    [emit]
  );

  const options = question.options || [];
  const col1Label = question.column1 || "Select";
  const col2Label = question.column2 || "Number";

  return (
    <ChoiceList>
      {rows.map((row, idx) => (
        <EntryCard key={idx}>
          <EntryCardRow>
            <div style={{ flex: 1 }}>
              <FieldLabel>{col1Label}</FieldLabel>
              <SelectWrapper
                value={row.selectVal?.value ?? ""}
                onChange={(e) => {
                  const opt = options.find((o) => o.value === e.target.value);
                  updateRow(idx, {
                    selectVal: opt
                      ? { value: opt.value, title: opt.title || opt.label }
                      : undefined,
                  });
                }}
              >
                <option value="">—</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.title || opt.label}
                  </option>
                ))}
              </SelectWrapper>
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>{col2Label}</FieldLabel>
              <TextInput
                type="number"
                min={question.min ?? 0}
                max={question.max}
                value={row.inputText}
                onChange={(e) => updateRow(idx, { inputText: e.target.value })}
                placeholder="0"
              />
            </div>
            {rows.length > 1 && (
              <RemoveButton
                type="button"
                onClick={() => removeRow(idx)}
                aria-label="Remove row"
              >
                ✕
              </RemoveButton>
            )}
          </EntryCardRow>
        </EntryCard>
      ))}
      {!question.auto_add && (
        <AddButton type="button" onClick={addRow}>
          + {question.addButtonText || "Add another"}
        </AddButton>
      )}
    </ChoiceList>
  );
};

export default ColumnsSelectNumberRenderer;
